import { useCallback, useMemo, type ChangeEvent } from 'react';
import {
  validateImageFile,
  formatFileSize,
  getImageNaturalDimensions,
} from '../../utils/fileHandler';
import { slugify } from '../../utils/slugify';
import { needsClientProcessing } from '../../utils/compressImage';
import { MAX_WEBP_DIMENSION } from '../../constants';
import type { OutputFormat } from '../../types/floorPlan.types';
import type { DiagramFileSettings } from '../../types/diagram.types';
import type { DiagramSummaryItemProps } from './types';

const getProcessingInfo = (fileSettings: DiagramFileSettings) => {
  const hasImages = Object.values(fileSettings.pageImages).some(
    (imageInfo) => imageInfo !== null,
  );
  if (!hasImages) return null;

  const willProcess = needsClientProcessing(
    {
      scaleFactor: fileSettings.scaleFactor,
      quality: fileSettings.quality,
      outputFormat: fileSettings.outputFormat,
    },
    'image/png',
  );

  if (!willProcess) return { status: 'passthrough' as const };

  if (fileSettings.outputFormat === 'webp') {
    for (const imageInfo of Object.values(fileSettings.pageImages)) {
      if (!imageInfo) continue;
      const outputWidth = Math.round(
        imageInfo.naturalWidth * fileSettings.scaleFactor,
      );
      const outputHeight = Math.round(
        imageInfo.naturalHeight * fileSettings.scaleFactor,
      );
      if (
        outputWidth > MAX_WEBP_DIMENSION ||
        outputHeight > MAX_WEBP_DIMENSION
      ) {
        return {
          status: 'webp-too-large' as const,
          outputWidth,
          outputHeight,
        };
      }
    }
  }

  return { status: 'will-compress' as const };
};

export const DiagramSummaryItem = ({
  diagramKey,
  info,
  projectId,
  fileSettings,
  onPageImageChange,
  onCompressionSettingsChange,
  onPdfChange,
}: DiagramSummaryItemProps) => {
  const sortedPages = [...info.pages].sort();

  const handleImageChange = useCallback(
    (pageIndex: number) => async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        onPageImageChange(diagramKey, pageIndex, null);
        return;
      }
      const error = validateImageFile(file);
      if (error) {
        onPageImageChange(diagramKey, pageIndex, null);
        e.target.value = '';
        return;
      }

      const dimensions = await getImageNaturalDimensions(file);
      onPageImageChange(diagramKey, pageIndex, {
        file,
        naturalWidth: dimensions.width,
        naturalHeight: dimensions.height,
      });
    },
    [diagramKey, onPageImageChange],
  );

  const handlePdfChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        onPdfChange(diagramKey, null);
        return;
      }
      if (file.type !== 'application/pdf') {
        onPdfChange(diagramKey, null);
        e.target.value = '';
        return;
      }
      onPdfChange(diagramKey, file);
    },
    [diagramKey, onPdfChange],
  );

  const processingInfo = useMemo(
    () => getProcessingInfo(fileSettings),
    [fileSettings],
  );

  const pdfFile = fileSettings.pdfFile;
  const pdfStatus = pdfFile ? formatFileSize(pdfFile) : 'No PDF selected';
  const pdfStatusColor = pdfFile ? 'text-[#3fb950]' : 'text-[#8b949e]';

  return (
    <div className="bg-[#0d1117] border border-[#30363d] rounded-md px-3 py-2.5 mb-1.5">
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span className="text-[#c9d1d9] font-semibold text-sm">
          {info.name}
        </span>
        <span className="text-[11px] text-[#8b949e]">
          {info.pages.size} page(s), {info.itemCount} item type(s),{' '}
          {info.occurrenceCount} occurrence(s)
        </span>
      </div>
      {/* PDF section */}
      <div className="flex items-center gap-2 flex-wrap mb-2 pb-2 border-b border-[#30363d]">
        <label className="text-[12px] text-[#8b949e]">PDF (multi-page):</label>
        <input
          type="file"
          accept=".pdf,application/pdf"
          className="text-sm text-[#8b949e] file:mr-2 file:py-1 file:px-3 file:rounded-md file:border file:border-[#30363d] file:bg-[#21262d] file:text-[#c9d1d9] file:text-sm file:cursor-pointer"
          onChange={handlePdfChange}
        />
        <span className={`text-[11px] ${pdfStatusColor}`}>{pdfStatus}</span>
      </div>
      {/* Compression settings (shared for all pages) */}
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-[#30363d] flex-wrap">
        <label className="text-[12px] text-[#8b949e]">Scale Factor:</label>
        <input
          type="number"
          className="bg-[#0d1117] border border-[#30363d] text-[#c9d1d9] px-2 py-1.5 rounded-md text-sm font-mono w-[100px] focus:outline-none focus:border-[#58a6ff]"
          value={fileSettings.scaleFactor}
          step={0.5}
          min={0}
          inputMode="decimal"
          onChange={(e) => {
            const value = parseFloat(e.target.value);
            if (Number.isNaN(value)) return;
            const clamped = Math.max(0.01, value);
            onCompressionSettingsChange(diagramKey, { scaleFactor: clamped });
          }}
        />
        <label className="text-[12px] text-[#8b949e] ml-3">Quality:</label>
        <input
          type="number"
          className="bg-[#0d1117] border border-[#30363d] text-[#c9d1d9] px-2 py-1.5 rounded-md text-sm font-mono w-[80px] focus:outline-none focus:border-[#58a6ff]"
          value={fileSettings.quality}
          min={1}
          max={100}
          onChange={(e) =>
            onCompressionSettingsChange(diagramKey, {
              quality: parseInt(e.target.value, 10) || 100,
            })
          }
        />
        <label className="text-[12px] text-[#8b949e] ml-3">Format:</label>
        <select
          className="bg-[#0d1117] border border-[#30363d] text-[#c9d1d9] px-2 py-1.5 rounded-md text-sm font-mono w-[100px] focus:outline-none focus:border-[#58a6ff]"
          value={fileSettings.outputFormat}
          onChange={(e) =>
            onCompressionSettingsChange(diagramKey, {
              outputFormat: e.target.value as OutputFormat,
            })
          }
        >
          <option value="webp">WebP</option>
          <option value="png">PNG</option>
          <option value="jpg">JPG</option>
        </select>
        {processingInfo?.status === 'will-compress' && (
          <span className="text-[11px] text-[#d29922] ml-2">
            Images will be compressed on server (scale=
            {fileSettings.scaleFactor}, quality={fileSettings.quality}, format=
            {fileSettings.outputFormat})
          </span>
        )}
        {processingInfo?.status === 'passthrough' && (
          <span className="text-[11px] text-[#8b949e] ml-2">
            Images will be uploaded as-is (no processing needed)
          </span>
        )}
        {processingInfo?.status === 'webp-too-large' && (
          <span className="text-[11px] text-[#f85149] font-semibold ml-2">
            WebP output would be {processingInfo.outputWidth}x
            {processingInfo.outputHeight} px, exceeding the {MAX_WEBP_DIMENSION}
            px WebP limit. Reduce scale factor or switch format.
          </span>
        )}
      </div>
      {/* Per-page image sections */}
      {sortedPages.map((pageName) => {
        const pageMatch = pageName.match(/page_(\d+)/);
        const pageIndex = pageMatch ? parseInt(pageMatch[1], 10) : 0;
        const sanitizedKey = slugify(diagramKey);
        const defaultImageUrl = `/smart-surveyor/projects/${projectId}/distribution_board_files/${sanitizedKey}/source/page_${pageIndex}.${fileSettings.outputFormat}`;

        const imageInfo = fileSettings.pageImages[pageIndex] ?? null;
        const fileStatus = imageInfo
          ? formatFileSize(imageInfo.file)
          : 'No file selected';
        const fileStatusColor = imageInfo ? 'text-[#3fb950]' : 'text-[#8b949e]';

        const dimsText =
          imageInfo && imageInfo.naturalWidth && imageInfo.naturalHeight
            ? `${imageInfo.naturalWidth} x ${imageInfo.naturalHeight} px`
            : '';

        const outputDimsText =
          imageInfo &&
          imageInfo.naturalWidth &&
          imageInfo.naturalHeight &&
          fileSettings.scaleFactor !== 1
            ? `-> ${Math.round(imageInfo.naturalWidth * fileSettings.scaleFactor)} x ${Math.round(imageInfo.naturalHeight * fileSettings.scaleFactor)} px`
            : '';

        return (
          <div key={`${diagramKey}_${pageIndex}`} className="mt-1.5">
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#58a6ff] text-[#0d1117] font-bold text-[11px] px-2 py-0.5 rounded">
                Page {pageIndex}
              </span>
              <span className="text-[11px] text-[#8b949e]">{pageName}</span>
              {dimsText && (
                <span className="text-[11px] text-[#58a6ff] font-mono ml-auto">
                  {dimsText} {outputDimsText}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mb-1">
              <label className="text-[12px] text-[#8b949e] w-[100px] flex-none">
                Image Path:
              </label>
              <span className="text-[12px] text-[#8b949e] font-mono truncate">
                {defaultImageUrl}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-[12px] text-[#8b949e]">Image File:</label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="text-sm text-[#8b949e] file:mr-2 file:py-1 file:px-3 file:rounded-md file:border file:border-[#30363d] file:bg-[#21262d] file:text-[#c9d1d9] file:text-sm file:cursor-pointer"
                onChange={handleImageChange(pageIndex)}
              />
              <span className={`text-[11px] ${fileStatusColor}`}>
                {fileStatus}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
